import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { customerApi } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';

const EMPTY = { company_name: '', contact_name: '', contact_email: '', package_name: 'Standard', token_limit_monthly: 1000000, storage_limit_mb: 1024 };

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Kunden "${name}" wirklich löschen?\n\nDabei werden alle zugehörigen Benutzer, Mailkonten und Mails unwiderruflich entfernt.`)) return;
    try {
      await customerApi.delete(id);
      load();
    } catch (err) {
      alert('Löschen fehlgeschlagen: ' + JSON.stringify(err.response?.data || err.message));
    }
  };

  const load = () => {
    setLoading(true);
    customerApi.list().then(({ data }) => setCustomers(data.results || data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await customerApi.create(form);
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      setError(JSON.stringify(err.response?.data || 'Fehler'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Kundenverwaltung</h1>
        <button onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          + Neuer Kunde
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="font-semibold mb-4">Neuen Kunden anlegen</h2>
          {error && <div className="mb-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            {[
              { name: 'company_name', label: 'Firmenname', required: true },
              { name: 'contact_name', label: 'Ansprechpartner', required: true },
              { name: 'contact_email', label: 'Kontakt-E-Mail', type: 'email', required: true },
              { name: 'package_name', label: 'Paket' },
              { name: 'token_limit_monthly', label: 'Token-Limit/Monat', type: 'number' },
              { name: 'storage_limit_mb', label: 'Speicherlimit (MB)', type: 'number' },
            ].map(({ name, label, type = 'text', required }) => (
              <div key={name}>
                <label htmlFor={`customer-${name}`} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input id={`customer-${name}`} type={type} required={required} value={form[name]}
                  onChange={(e) => setForm({ ...form, [name]: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </div>
            ))}
            <div className="col-span-2 flex gap-3 justify-end">
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                Abbrechen
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Speichern...' : 'Anlegen'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {loading ? <LoadingSpinner /> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
              <tr>
                {['Firma', 'Kontakt', 'Paket', 'Benutzer', 'Mailkonten', 'Status', 'Aktionen'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {customers.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">
                    <Link to={`/admin/kunden/${c.id}`} className="text-gray-900 hover:text-blue-600 hover:underline">
                      {c.company_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{c.contact_email}</td>
                  <td className="px-4 py-3 text-gray-500">{c.package_name}</td>
                  <td className="px-4 py-3 text-gray-500">{c.user_count}</td>
                  <td className="px-4 py-3 text-gray-500">{c.mail_account_count}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 flex items-center gap-3">
                    <Link to={`/admin/kunden/${c.id}`}
                      className="text-blue-600 hover:text-blue-800 font-medium">
                      Öffnen
                    </Link>
                    <button
                      onClick={() => handleDelete(c.id, c.company_name)}
                      className="text-red-500 hover:text-red-700 text-xs font-medium">
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
